import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Entrenos from '@/pages/Entrenos';
import Usuarios from '@/pages/Usuarios';
import Galeria from '@/pages/Galeria';
import AdminTrainings from '@/pages/AdminTrainings';
import Perfil from '@/pages/Perfil';
import Notificaciones from '@/pages/Notificaciones';
import Estadisticas from '@/pages/Estadisticas';
import Noticias from '@/pages/Noticias';
import Recetas from '@/pages/Recetas';
import Sponsors from '@/pages/Sponsors';
import CienTiros from '@/pages/CienTiros';
import MKTitan from '@/pages/MKTitan';
import Words from '@/pages/Words';
import Impostor from '@/pages/Impostor';
import Pedidos from '@/pages/Pedidos';
import GestionAnuncios from '@/pages/GestionAnuncios';
import Tiempo from '@/pages/Tiempo';
import MKDietal from '@/pages/MKDietal';
import MKHabits from '@/pages/MKHabits';
import MKDietalAdmin from '@/pages/MKDietalAdmin';
import Cuadros from '@/pages/Cuadros';
import HallOfFame from '@/pages/HallOfFame';
import NotificacionesPopup from '@/components/NotificacionesPopup';
import IOSInstallBanner from '@/components/IOSInstallPrompt';
import { Toaster } from 'sonner';
import MKVoice from '@/pages/MKVoice';
import MKLive from '@/pages/MKLive';
import CoachCheckins from '@/pages/CoachCheckins';
import ControlSesiones from '@/pages/ControlSesiones';
import SesionesFuerza from '@/pages/SesionesFuerza';
import MKPetLab from '@/pages/TraductorGatos';
import Backups from '@/pages/Backups';
import ActivityPage from '@/pages/ActivityPage';
import MuroKlan from '@/pages/MuroKlan';
import RetosKlan from '@/pages/RetosKlan';
import MallorcaKlanLanding from '@/pages/MallorcaKlanLanding';
import ClientPlusDashboard from '@/pages/ClientPlusDashboard';
import MisRutinas from '@/pages/MisRutinas';
import ProgresoCliente from '@/pages/ProgresoCliente';
import Descargar from '@/pages/Descargar';
import ControlFamiliar from '@/pages/ControlFamiliar';
import PlanesEntrenamiento from '@/pages/PlanesEntrenamiento';
import CalendarioPreparacion from '@/pages/CalendarioPreparacion';
import EntrenoTimer from '@/pages/EntrenoTimer';
import UpdateBanner from '@/components/UpdateBanner';
import FailoverManager from '@/components/FailoverManager';
import { AuthProvider, useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;
export { BACKEND_URL };

// Configurar axios para incluir el token en todas las peticiones
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Notificar al FailoverManager cuando una petición falla por red (no por HTTP)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error.response) {
      window.dispatchEvent(new Event('mk-api-network-error'));
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  
  // Esperar a que termine la verificación de autenticación
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
}

function EntrenadorRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }
  
  return user && user.role === 'entrenador' ? children : <Navigate to="/" />;
}

function ProRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }
  
  return user && (user.es_pro || user.role === 'entrenador') ? children : <Navigate to="/" />;
}

function ClientPlusOrProRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }
  return user && (user.es_pro || user.role === 'entrenador' || user.role === 'client_plus') ? children : <Navigate to="/" />;
}

// Wrapper que incluye el popup de notificaciones
function AppContent() {
  const { user } = useAuth();
  const isPro = user?.es_pro || user?.role === 'entrenador';
  const isClientPlus = user?.role === 'client_plus';

  // Apply Valhalla theme globally, synced with MK Valhalla dark/light toggle
  useEffect(() => {
    document.documentElement.classList.add('valhalla-theme');
    const syncTheme = () => {
      const isDark = localStorage.getItem('valhalla_dark') !== 'false';
      document.documentElement.classList.toggle('valhalla-light', !isDark);
    };
    syncTheme();
    window.addEventListener('storage', syncTheme);
    window.addEventListener('valhalla-theme-change', syncTheme);
    return () => {
      window.removeEventListener('storage', syncTheme);
      window.removeEventListener('valhalla-theme-change', syncTheme);
    };
  }, []);

  // Liberar cualquier lock de orientación heredado de versiones antiguas de la PWA
  useEffect(() => {
    try {
      if (window.screen && window.screen.orientation && typeof window.screen.orientation.unlock === 'function') {
        window.screen.orientation.unlock();
      }
    } catch { /* ignore */ }
  }, []);
  
  return (
    <div className={isPro ? 'app-pro' : ''}>
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: 'rgba(20,20,20,0.95)',
            border: '1px solid rgba(212,175,55,0.4)',
            color: '#fff',
            fontWeight: 500,
          },
        }}
      />
      {user && <NotificacionesPopup />}
      {user && <IOSInstallBanner />}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/descargar" element={<Descargar />} />
        <Route path="/mallorcaklan" element={<MallorcaKlanLanding />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/entrenos"
          element={
            <PrivateRoute>
              <EntrenadorRoute>
                <Entrenos />
              </EntrenadorRoute>
            </PrivateRoute>
          }
        />
        <Route
          path="/usuarios"
          element={
            <PrivateRoute>
              <EntrenadorRoute>
                <Usuarios />
              </EntrenadorRoute>
            </PrivateRoute>
          }
        />
          <Route
            path="/galeria"
            element={
              <PrivateRoute>
                <Galeria />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/trainings"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <AdminTrainings />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <PrivateRoute>
                <Perfil />
              </PrivateRoute>
            }
          />
          <Route
            path="/notificaciones"
            element={
              <PrivateRoute>
                <Notificaciones />
              </PrivateRoute>
            }
          />
          <Route
            path="/estadisticas"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <Estadisticas />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/noticias"
            element={
              <PrivateRoute>
                <Noticias />
              </PrivateRoute>
            }
          />
          <Route
            path="/recetas"
            element={
              <PrivateRoute>
                <Recetas />
              </PrivateRoute>
            }
          />
          <Route
            path="/sponsors"
            element={
              <PrivateRoute>
                <Sponsors />
              </PrivateRoute>
            }
          />
          <Route
            path="/100-tiros"
            element={
              <PrivateRoute>
                <ProRoute>
                  <CienTiros />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/cronometro-entreno"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <EntrenoTimer />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/mk-titan"
            element={
              <PrivateRoute>
                <ProRoute>
                  <MKTitan />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/words"
            element={
              <PrivateRoute>
                <ProRoute>
                  <Words />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/impostor"
            element={
              <PrivateRoute>
                <ProRoute>
                  <Impostor />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/traductor-gatos"
            element={
              <PrivateRoute>
                <ProRoute>
                  <MKPetLab />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/pedidos"
            element={
              <PrivateRoute>
                <Pedidos />
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/anuncios"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <GestionAnuncios />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/tiempo"
            element={
              <PrivateRoute>
                <Tiempo />
              </PrivateRoute>
            }
          />
          <Route
            path="/mkdietal"
            element={
              <PrivateRoute>
                <ClientPlusOrProRoute>
                  <MKDietal />
                </ClientPlusOrProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/mkdietal/admin"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <MKDietalAdmin />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/coach/checkins"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <CoachCheckins />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/mk-habits"
            element={
              <PrivateRoute>
                <ClientPlusOrProRoute>
                  <MKHabits />
                </ClientPlusOrProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/cuadros"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <Cuadros />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/control-familiar"
            element={
              <PrivateRoute>
                <ControlFamiliar />
              </PrivateRoute>
            }
          />
          <Route
            path="/backups"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <Backups />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/actividad"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <ActivityPage />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/hall-of-fame"
            element={
              <PrivateRoute>
                <HallOfFame />
              </PrivateRoute>
            }
          />
          <Route
            path="/mk-voice"
            element={
              <PrivateRoute>
                <ProRoute>
                  <MKVoice />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/control-sesiones"
            element={
              <PrivateRoute>
                <ControlSesiones />
              </PrivateRoute>
            }
          />
          <Route
            path="/sesiones-fuerza"
            element={
              <PrivateRoute>
                <SesionesFuerza />
              </PrivateRoute>
            }
          />
          <Route
            path="/mis-rutinas"
            element={
              <PrivateRoute>
                <MisRutinas />
              </PrivateRoute>
            }
          />
          <Route
            path="/progreso-clientes"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <ProgresoCliente />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/mk-live"
            element={
              <PrivateRoute>
                <ProRoute>
                  <MKLive />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/mk-live/:channelId"
            element={
              <PrivateRoute>
                <ProRoute>
                  <MKLive />
                </ProRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/muro"
            element={
              <PrivateRoute>
                <MuroKlan />
              </PrivateRoute>
            }
          />
          <Route
            path="/retos"
            element={
              <PrivateRoute>
                <RetosKlan />
              </PrivateRoute>
            }
          />
          <Route
            path="/planes"
            element={
              <PrivateRoute>
                <EntrenadorRoute>
                  <PlanesEntrenamiento />
                </EntrenadorRoute>
              </PrivateRoute>
            }
          />
          <Route
            path="/mi-preparacion"
            element={
              <PrivateRoute>
                <CalendarioPreparacion />
              </PrivateRoute>
            }
          />
        </Routes>
      </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
        <UpdateBanner />
        <FailoverManager />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;